import React, { useEffect, useState } from "react";
import {
    Box,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Grid,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { BsEye } from "react-icons/bs";
import PageHeader1 from "../../components/common/PageHeader1";
import axios from "axios";
import { handleUnauthorized } from "../../TokenAuth/auth";
import { formatUTCDate } from "../../utils/dateUtils";

function PoojaTransactionReport() {
    const [rowsData, setRowsData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [pagination, setPagination] = useState({
        page: 0,
        pageSize: 10,
    });
    const [total, setTotal] = useState(0);

    // 🔍 SEARCH
    const [searchType, setSearchType] = useState("poojaName");
    const [searchValue, setSearchValue] = useState("");
    const [appliedSearchType, setAppliedSearchType] = useState("poojaName");
    const [appliedSearchValue, setAppliedSearchValue] = useState("");

    // 🔍 REFERRAL FILTER
    const [referralFilter, setReferralFilter] = useState("all");
    const [appliedReferralFilter, setAppliedReferralFilter] = useState("all");

    const [isExporting, setIsExporting] = useState(false);

    // MODAL STATE
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);

    const handleOpenViewDialog = (transaction) => {
        setSelectedTransaction(transaction);
        setViewDialogOpen(true);
    };

    const handleCloseViewDialog = () => {
        setViewDialogOpen(false);
        setSelectedTransaction(null);
    };

    const token = localStorage.getItem("astroVani-admin-token");
    const BASE_URL = process.env.REACT_APP_BASEURL;

    // ================= API =================
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const params = {
                    page: pagination.page + 1,
                    limit: pagination.pageSize,
                };

                // 🔍 SEARCH PARAM
                if (appliedSearchValue) {
                    params[appliedSearchType] = appliedSearchValue;
                }

                // 🔍 REFERRAL FILTER
                if (appliedReferralFilter === "referred") {
                    params.isReferred = true;
                } else if (appliedReferralFilter === "without-referred") {
                    params.isReferred = false;
                }

                const res = await axios.get(
                    `${BASE_URL}/admin/pooja/transaction`,
                    {
                        params,
                        headers: { Authorization: token },
                    }
                );

                setRowsData(res?.data?.data || []);
                setTotal(res?.data?.paginationDetail?.totalRecords || 0);
            } catch (err) {
                if (err?.response?.status === 401) handleUnauthorized();
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [pagination, appliedSearchType, appliedSearchValue, BASE_URL, token]);

    // ================= APPLY =================
    const handleApply = () => {
        setAppliedSearchType(searchType);
        setAppliedSearchValue(searchValue);
        setAppliedReferralFilter(referralFilter);
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    // ================= RESET =================
    const handleReset = () => {
        setSearchType("poojaName");
        setSearchValue("");
        setAppliedSearchType("poojaName");
        setAppliedSearchValue("");
        setReferralFilter("all");
        setAppliedReferralFilter("all");
        setPagination({ page: 0, pageSize: 10 });
    };

    // ================= EXPORT =================
    const handleExport = async () => {
        setIsExporting(true);
        try {
            const params = new URLSearchParams();

            if (appliedSearchValue) {
                params.append(appliedSearchType, appliedSearchValue);
            }
            if (appliedReferralFilter === "referred") {
                params.append("isReferred", true);
            } else if (appliedReferralFilter === "without-referred") {
                params.append("isReferred", false);
            }

            const response = await fetch(
                `${BASE_URL}/admin/pooja/transactionExport?${params.toString()}`,
                {
                    headers: { Authorization: token },
                }
            );

            if (!response.ok) {
                if (response.status === 401) handleUnauthorized();
                throw new Error("Export failed");
            }

            const result = await response.json();
            const data = result?.data || result || [];

            const headers = [
                "S.No",
                "Transaction ID",
                "Order ID",
                "User Name",
                "Mobile Number",
                "Amount",
                "Wallet Portion",
                "Razorpay Portion",
                "Pooja Name",
                "Astrologer Name",
                "Astrologer Share",
                "Astrologer Share %",
                "Company Share",
                "Company Share %",
                "Referral Astrologer Amount",
                "Referred Astrologer Name",
                "Referral Percentage",
                "Status",
                "Description",
                "Created Date",
                "Created Time",
            ];

            const rows = data.map((item, i) => [
                i + 1,
                item.transactionId || "-",
                item.orderId || "-",
                item.userName || "Deleted User",
                item.mobileNumber || "-",
                item.amount || 0,
                item.walletPortion || 0,
                item.razorpayPortion || 0,
                item.poojaName || "-",
                item.astrologerName || "-",
                item.astrologerShare ?? "-",
                item.astrologerSharePercentage ?? "-",
                item.companyShare ?? "-",
                item.companySharePercentage ?? "-",
                item.referralAstrologerAmount ?? "-",
                item.referredAstrologerName || "-",
                item.referralPercentage ?? "-",
                item.status || "-",
                `"${item.description || "-"}"`,
                formatUTCDate(item.createdAt, { format: "date" }),
                formatUTCDate(item.createdAt, { format: "time" }),
            ]);

            const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");

            const blob = new Blob([csv], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = "pooja-transaction-report.csv";
            a.click();
        } catch (err) {
            console.error(err);
            alert("Export failed");
        } finally {
            setIsExporting(false);
        }
    };

    // ================= COLUMNS =================
    const columns = [
        { field: "sono", headerName: "S.No", width: 80, sortable: false, filterable: false, renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1 + pagination.page * pagination.pageSize },
        {
            field: "actions",
            headerName: "Action",
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <IconButton color="primary" onClick={() => handleOpenViewDialog(params.row)} title="View Details">
                    <BsEye style={{ fontSize: "1.2rem" }} />
                </IconButton>
            )
        },
        { field: "transactionId", headerName: "Transaction ID", width: 220 },
        { field: "orderId", headerName: "Order ID", width: 180 },
        {
            field: "userName", headerName: "User Name", width: 180,
            renderCell: (params) => params.value || "Deleted User"
        },
        {
            field: "mobileNumber", headerName: "Mobile", width: 130,
            renderCell: (params) => params.value || "-"
        },
        { field: "amount", headerName: "Amount", width: 110 },
        { field: "walletPortion", headerName: "Wallet Portion", width: 130 },
        { field: "razorpayPortion", headerName: "Razorpay Portion", width: 150 },
        { field: "poojaName", headerName: "Pooja Name", width: 150 },
        { field: "astrologerName", headerName: "Astrologer Name", width: 180 },
        { field: "astrologerShare", headerName: "Astrologer Share", width: 140 },
        { field: "astrologerSharePercentage", headerName: "Astrologer Share %", width: 140 },
        { field: "companyShare", headerName: "Company Share", width: 140 },
        { field: "companySharePercentage", headerName: "Company Share %", width: 140 },
        { field: "referralAstrologerAmount", headerName: "Referral Astro Amount", width: 170 },
        { field: "referredAstrologerName", headerName: "Referred Astro Name", width: 180, renderCell: (params) => params.value || "-" },
        { field: "referralPercentage", headerName: "Referral %", width: 110 },
        { field: "status", headerName: "Status", width: 120 },
        {
            field: "createdAt",
            headerName: "Created Date",
            width: 140,
            renderCell: (params) => params.value ? formatUTCDate(params.value, { format: "date" }) : "-"
        },
        {
            field: "createdTime",
            headerName: "Created Time",
            width: 130,
            renderCell: (params) => params.row.createdAt ? formatUTCDate(params.row.createdAt, { format: "time" }) : "-"
        },
        { field: "description", headerName: "Description", width: 400 }
    ];

    return (
        <div className="container-xxl">
            <PageHeader1 pagetitle="Pooja Transaction Report" />

            <div className="row g-3 mb-3">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {/* SEARCH BAR */}
                            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel id="search-type-label" sx={{ top: '-4px' }}>Search By</InputLabel>
                                    <Select
                                        labelId="search-type-label"
                                        value={searchType}
                                        label="Search By"
                                        onChange={(e) => {
                                            setSearchType(e.target.value);
                                            setSearchValue("");
                                        }}
                                        sx={{ height: 35 }}
                                    >
                                        <MenuItem value="poojaName">Pooja Name</MenuItem>
                                        <MenuItem value="orderId">Order ID</MenuItem>
                                        <MenuItem value="mobileNumber">Mobile Number</MenuItem>
                                        <MenuItem value="userName">User Name</MenuItem>
                                        <MenuItem value="astrologerName">Astrologer Name</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Search Value"
                                    value={searchValue}
                                    size="small"
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    InputProps={{ sx: { height: 35 } }}
                                    InputLabelProps={{ sx: { top: '-4px' } }}
                                    sx={{ minWidth: 250 }}
                                />

                                <Button
                                    variant="contained"
                                    onClick={handleApply}
                                    sx={{ minWidth: 100, height: 35 }}
                                >
                                    Apply
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleReset}
                                    sx={{ minWidth: 100, height: 35 }}
                                >
                                    Reset
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleExport}
                                    disabled={isExporting}
                                    sx={{ height: 35 }}
                                >
                                    {isExporting ? "Exporting..." : "Export"}
                                </Button>
                            </Box>
                            <Box>
                                <Tabs
                                    value={appliedReferralFilter}
                                    onChange={(e, newValue) => {
                                        setReferralFilter(newValue);
                                        setAppliedReferralFilter(newValue);
                                        setPagination((prev) => ({ ...prev, page: 0 }));
                                    }}
                                    aria-label="referral tabs"
                                    sx={{ borderTop: "1px solid #535252", borderBottom: "1px solid #535252", py: 1 }}
                                >
                                    <Tab label="All" value="all" />
                                    <Tab label="Referred" value="referred" />
                                    <Tab label="Without Referred" value="without-referred" />
                                </Tabs>
                            </Box>

                            {/* DATA GRID */}
                            <Box sx={{ height: 600, width: "100%" }}>
                                <DataGrid
                                    rows={rowsData}
                                    columns={columns}
                                    getRowId={(row) => row._id || Math.random()}
                                    rowCount={total}
                                    loading={loading}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    paginationModel={pagination}
                                    paginationMode="server"
                                    onPaginationModelChange={setPagination}
                                    disableRowSelectionOnClick
                                />
                            </Box>
                        </div>
                    </div>
                </div>
            </div>

            {/* VIEW DETAILS MODAL */}
            <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
                <DialogTitle>Transaction Details</DialogTitle>
                <DialogContent dividers>
                    {selectedTransaction && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Transaction ID</Typography>
                                <Typography variant="body1">{selectedTransaction.transactionId || "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Order ID</Typography>
                                <Typography variant="body1">{selectedTransaction.orderId || "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">User Name</Typography>
                                <Typography variant="body1">{selectedTransaction.userName || "Deleted User"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Mobile Number</Typography>
                                <Typography variant="body1">{selectedTransaction.mobileNumber || "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                                <Typography variant="body1">{selectedTransaction.amount || 0}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Wallet Portion</Typography>
                                <Typography variant="body1">{selectedTransaction.walletPortion || 0}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Razorpay Portion</Typography>
                                <Typography variant="body1">{selectedTransaction.razorpayPortion || 0}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Pooja Name</Typography>
                                <Typography variant="body1">{selectedTransaction.poojaName || "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Astrologer Name</Typography>
                                <Typography variant="body1">{selectedTransaction.astrologerName || "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Astrologer Share</Typography>
                                <Typography variant="body1">{selectedTransaction.astrologerShare ?? "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Astrologer Share %</Typography>
                                <Typography variant="body1">{selectedTransaction.astrologerSharePercentage ?? "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Company Share</Typography>
                                <Typography variant="body1">{selectedTransaction.companyShare ?? "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Company Share %</Typography>
                                <Typography variant="body1">{selectedTransaction.companySharePercentage ?? "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Referral Astro Amount</Typography>
                                <Typography variant="body1">{selectedTransaction.referralAstrologerAmount ?? "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Referred Astro Name</Typography>
                                <Typography variant="body1">{selectedTransaction.referredAstrologerName || "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Referral %</Typography>
                                <Typography variant="body1">{selectedTransaction.referralPercentage ?? "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                                <Typography variant="body1">{selectedTransaction.status || "-"}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                                <Typography variant="body1">
                                    {selectedTransaction.createdAt ? `${formatUTCDate(selectedTransaction.createdAt, { format: "date" })} ${formatUTCDate(selectedTransaction.createdAt, { format: "time" })}` : "-"}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                                <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>{selectedTransaction.description || "-"}</Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseViewDialog} color="primary">Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default PoojaTransactionReport;
